import { faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMemo, useState, type ReactNode } from 'react';
import styled, { css } from 'styled-components';

import { COLORS } from '@/shared/constants/tasksColors';

import type {
  CardsGenericasColors,
  CardsGenericasMenuItem,
  CardsGenericasProps,
} from '../../types';

const sizingVars = css`
  --widthCard: calc(25vw - 70px);
  --heightCard: calc(var(--widthCard) / 16 * 9);
  @media (max-aspect-ratio: 4/3) {
    --widthCard: calc(50vw - 80px);
  }
  @media (max-aspect-ratio: 7/10) {
    --widthCard: calc(100vw - 100px);
  }
`;

const chevronStroke = '#2c2926';

function defaultColors(
  colorMode: 'light' | 'dark'
): Required<
  Pick<CardsGenericasColors, 'bg' | 'detail' | 'detailColor' | 'tabText' | 'contentText'>
> {
  const modo = colorMode === 'dark' ? ('Dark' as const) : ('Light' as const);
  const text = COLORS.FONTS['90%'][modo];
  return {
    bg: COLORS.BG[modo],
    detail: COLORS.PRIMARY[modo].REGULAR,
    detailColor: COLORS.BUTTONS.PRIMARIO[modo],
    tabText: text,
    contentText: text,
  };
}

const ImageArea = styled.div`
  width: 100%;
  border-radius: 15px;
  height: 100%;
  position: relative;
  overflow: hidden;
`;

/** Chevron Font Awesome (pro-regular), centrado por el propio glifo y el flex del botón. */
const ChevronFa = styled(FontAwesomeIcon)<{ $open: boolean }>`
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  display: block;
  margin: 0;
  padding: 0;
  color: #fff;
  transform-origin: 50% 50%;
  transition:
    transform 0.5s ease-in-out,
    color 0.3s ease;
  transform: rotate(${({ $open }) => ($open ? '180deg' : '0deg')});

  ${({ $open }) => css`
    @media (max-aspect-ratio: 7/10) {
      transform: rotate(${$open ? '270deg' : '90deg'});
    }
  `}
`;

const ToggleButton = styled.button<{ $detail: string; $detailColor: string; $bg: string }>`
  position: absolute;
  right: -25px;
  width: 50px;
  height: 50px;
  background: ${({ $detail }) => $detail};
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
  border-radius: 50px;
  box-shadow: 0 0 0 3px ${({ $bg }) => $bg};
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${({ $detailColor }) => $detailColor};
    ${ChevronFa} {
      color: ${({ $bg }) => $bg};
    }
  }

  @media (max-aspect-ratio: 7/10) {
    top: 50%;
  }
`;

const DetailsPanel = styled.div<{ $detail: string }>`
  width: calc(100% - var(--widthCard) / 2);
  height: 100%;
  padding: 15px 15px 10px 15px;
  display: flex;
  flex-direction: column;
  border-radius: 0 15px 15px 0;
  box-shadow: inset 0 0 0 1px ${({ $detail }) => $detail};
`;

const HeaderRow = styled.div`
  display: flex;
  overflow-x: auto;
  overflow-y: hidden;
  gap: 15px;
  height: 25px;
  width: 100%;
`;

const TabButton = styled.button<{
  $active: boolean;
  $detail: string;
  $detailColor: string;
  $tabText: string;
}>`
  min-width: max-content;
  cursor: pointer;
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;
  font: inherit;
  font-weight: 600;
  font-size: 14px;
  line-height: 1.2;
  color: ${({ $tabText }) => $tabText};
  text-align: left;

  &:hover {
    color: ${({ $detailColor }) => $detailColor};
  }

  ${({ $active, $detail }) =>
    $active &&
    css`
      color: ${$detail};
      border-bottom: 2px solid ${$detail};
    `}
`;

const ChildrenSlot = styled.div<{ $contentText: string }>`
  width: 100%;
  height: 100%;
  padding: 5px 0;
  overflow-y: auto;
  color: ${({ $contentText }) => $contentText};
`;

const Root = styled.div<{
  $open: boolean;
  $bg: string;
  $detail: string;
  $detailColor: string;
}>`
  ${sizingVars}
  width: var(--widthCard);
  height: var(--heightCard);
  position: relative;
  border-radius: 15px;
  transition: all 0.5s ease-in-out;
  margin: 15px 30px 15px 15px;
  background: ${({ $bg }) => $bg};
  box-shadow: none;

  @media (max-aspect-ratio: 7/10) {
    height: auto;
    min-height: var(--heightCard);
  }

  ${({ $open, $bg, $detail, $detailColor }) =>
    $open &&
    css`
      display: flex;
      width: calc(var(--widthCard) * 2 + 70px);

      ${ImageArea} {
        border-radius: 15px 0 0 15px;
        width: calc(var(--widthCard) / 2);
      }

      ${ToggleButton} {
        background: ${$detailColor};
        &:hover {
          background: ${$detail};
          ${ChevronFa} {
            color: ${chevronStroke};
          }
        }
      }

      @media (max-aspect-ratio: 7/10) {
        flex-direction: column;

        ${ImageArea} {
          width: var(--widthCard);
          height: calc(var(--widthCard) / 16 * 9);
          border-radius: 15px 15px 0 0;
        }

        ${ToggleButton} {
          top: 28%;
        }

        ${DetailsPanel} {
          width: var(--widthCard);
          height: var(--heightCard);
        }
      }
    `}
`;

function renderMenuTabs(
  menus: CardsGenericasMenuItem[] | undefined,
  activeIndex: number | undefined,
  setActiveIndex: ((index: number) => void) | undefined,
  detail: string,
  detailColor: string,
  tabText: string
): ReactNode {
  return menus?.map((menu) => (
    <TabButton
      key={menu.index}
      type="button"
      $active={activeIndex === menu.index}
      $detail={detail}
      $detailColor={detailColor}
      $tabText={tabText}
      onClick={() => setActiveIndex?.(menu.index)}
    >
      {menu.title}
    </TabButton>
  ));
}

/**
 * Tarjeta genérica con vista previa, botón lateral para expandir y panel con pestañas opcionales.
 * Estilos con `styled-components` (sin clases BEM); colores por defecto desde `COLORS` según `colorMode`.
 *
 * @example
 * ```tsx
 * const [open, setOpen] = useState(false);
 * <CardsGenericas
 *   colorMode="dark"
 *   openCard={open}
 *   setOpenCard={setOpen}
 *   initialView={<img src="..." alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
 * >
 *   <p>Contenido</p>
 * </CardsGenericas>
 * ```
 */
export function CardsGenericas({
  initialView,
  menus,
  activeIndex,
  setActiveIndex,
  children,
  openCard: openCardProp,
  setOpenCard: setOpenCardProp,
  colors: colorsPartial,
  colorMode = 'light',
}: CardsGenericasProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const controlled = openCardProp !== undefined && setOpenCardProp !== undefined;
  const isOpen = controlled ? Boolean(openCardProp) : uncontrolledOpen;

  const palette = useMemo(() => {
    const base = defaultColors(colorMode);
    const partial = colorsPartial ?? {};
    return {
      ...base,
      ...partial,
      tabText: partial.tabText ?? base.tabText,
      contentText: partial.contentText ?? base.contentText,
    };
  }, [colorMode, colorsPartial]);

  const toggle = () => {
    if (controlled) {
      setOpenCardProp!(!openCardProp);
    } else {
      setUncontrolledOpen((v) => !v);
    }
  };

  return (
    <Root
      data-open={isOpen ? 'true' : 'false'}
      $open={isOpen}
      $bg={palette.bg}
      $detail={palette.detail}
      $detailColor={palette.detailColor}
    >
      <ImageArea>{initialView}</ImageArea>
      <ToggleButton
        type="button"
        aria-expanded={isOpen}
        aria-label={isOpen ? 'Cerrar panel' : 'Abrir panel'}
        $detail={palette.detail}
        $detailColor={palette.detailColor}
        $bg={palette.bg}
        onClick={toggle}
      >
        <ChevronFa icon={faChevronRight} $open={isOpen} aria-hidden />
      </ToggleButton>

      {isOpen && (
        <DetailsPanel $detail={palette.detail}>
          {menus && menus.length > 0 && (
            <HeaderRow>
              {renderMenuTabs(
                menus,
                activeIndex,
                setActiveIndex,
                palette.detail,
                palette.detailColor,
                palette.tabText
              )}
            </HeaderRow>
          )}
          <ChildrenSlot $contentText={palette.contentText}>{children}</ChildrenSlot>
        </DetailsPanel>
      )}
    </Root>
  );
}
